import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  QrCode,
  Copy,
  CheckCircle,
  Trash2,
  Eye,
  User,
  Phone,
  DollarSign,
  Calendar,
  CreditCard,
  Package,
  Hash,
  XCircle,
} from "lucide-react";
import pixService from "@/services/pixService";

/**
 * Modal de detalhes do pagamento
 * Exibe todas as informações do pagamento e ações disponíveis
 */
const PaymentDetailsModal = ({
  open,
  onOpenChange,
  payment,
  onViewQRCode,
  onCopy,
  onMarkReceived,
  onDelete,
}) => {
  if (!payment) return null;

  const isReceived =
    payment.status === "S" || payment.recebido || payment.status === "RECEIVED";

  const getStatusBadge = () => {
    if (isReceived) {
      return (
        <Badge className="text-sm bg-green-100 text-green-800 border-green-200 border">
          <CheckCircle className="h-3 w-3 mr-1" />
          Pago
        </Badge>
      );
    }
    return (
      <Badge className="text-sm bg-yellow-100 text-yellow-800 border-yellow-200 border">
        <XCircle className="h-3 w-3 mr-1" />
        Pendente
      </Badge>
    );
  };

  const getPaymentIcon = (method) => {
    const methodLower = (method || "").toLowerCase();
    if (methodLower.includes("pix")) return <QrCode className="h-4 w-4" />;
    if (
      methodLower.includes("cartão") ||
      methodLower.includes("cartao") ||
      methodLower.includes("card")
    )
      return <CreditCard className="h-4 w-4" />;
    return <DollarSign className="h-4 w-4" />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="h-6 w-6 text-[#c9332C]" />
            Detalhes do Pagamento
          </DialogTitle>
          <DialogDescription>
            Informações completas e ações disponíveis
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Status e Valor */}
          <Card className="bg-gradient-to-r from-gray-50 to-gray-100">
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <div>{getStatusBadge()}</div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 mb-1">Valor Total</div>
                  <div className="text-2xl font-bold text-green-600">
                    {pixService.formatCurrency(
                      payment.amount || payment.valor || 0
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Informações Principais */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {payment.orderCode && (
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-start gap-2">
                    <Package className="h-4 w-4 text-indigo-600 mt-1" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 mb-1">Pedido</p>
                      <p className="text-sm font-bold text-gray-900">
                        #{payment.orderCode}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {payment.pay_method && (
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-start gap-2">
                    {getPaymentIcon(payment.pay_method)}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 mb-1">
                        Método de Pagamento
                      </p>
                      <p className="text-sm font-bold text-gray-900">
                        {payment.pay_method}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {payment.name && (
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-start gap-2">
                    <User className="h-4 w-4 text-green-600 mt-1" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 mb-1">Cliente</p>
                      <p className="text-sm font-bold text-gray-900 break-words">
                        {payment.name}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {payment.contact && (
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-start gap-2">
                    <Phone className="h-4 w-4 text-teal-600 mt-1" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 mb-1">Contato</p>
                      <p className="text-sm font-bold text-gray-900">
                        {pixService.formatPhone(payment.contact)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {payment.createdAt && (
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-start gap-2">
                    <Calendar className="h-4 w-4 text-amber-600 mt-1" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 mb-1">
                        Data de Criação
                      </p>
                      <p className="text-sm font-bold text-gray-900">
                        {pixService.formatDate(payment.createdAt)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {payment.amount && (
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-start gap-2">
                    <DollarSign className="h-4 w-4 text-purple-600 mt-1" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 mb-1">Valor</p>
                      <p className="text-sm font-bold text-gray-900">
                        {pixService.formatCurrency(
                          payment.amount || payment.valor || 0
                        )}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* ID da Transação */}
          {payment.txid && (
            <Card>
              <CardContent className="p-3">
                <div className="flex items-start gap-2">
                  <Hash className="h-4 w-4 text-[#c9332C] mt-1" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 mb-1">
                      ID da Transação
                    </p>
                    <p className="text-xs font-mono text-gray-900 break-all bg-gray-50 p-2 rounded border">
                      {payment.txid}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ações */}
          <div className="flex flex-col gap-2 pt-4 border-t">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Ações</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {(payment.qrcodeUrl ||
                payment.qrcode ||
                payment.imagemQrcode) && (
                <Button
                  variant="outline"
                  size="lg"
                  className="text-blue-600 hover:bg-blue-50 border-blue-300 h-12"
                  onClick={() => {
                    onViewQRCode(payment);
                    onOpenChange(false);
                  }}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Ver QR Code
                </Button>
              )}

              {payment.txid && (
                <Button
                  variant="outline"
                  size="lg"
                  className="text-green-600 hover:bg-green-50 border-green-300 h-12"
                  onClick={() => {
                    onCopy(payment);
                  }}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copiar Código
                </Button>
              )}

              {!isReceived && (
                <Button
                  variant="outline"
                  size="lg"
                  className="text-emerald-600 hover:bg-emerald-50 border-emerald-300 h-12"
                  onClick={() => {
                    onMarkReceived(payment);
                    onOpenChange(false);
                  }}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Marcar como Pago
                </Button>
              )}

              <Button
                variant="outline"
                size="lg"
                className="text-red-600 hover:bg-red-50 border-red-300 h-12"
                onClick={() => {
                  onDelete(payment);
                  onOpenChange(false);
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir Pagamento
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDetailsModal;
